import { KonvaEventObject } from "konva/types/Node";
import { useRef } from "react";

interface UsePreciseDoubleClickOptions {
  timeThreshold?: number;
  distanceThreshold?: number;
}

const getEventPosition = (event: KonvaEventObject<MouseEvent>) => ({ x: event.evt.offsetX, y: event.evt.offsetY });

/**
 * Hook that detects a precise double click event on konva elements.
 * It takes into account the time and distance between two clicks
 */
export const usePreciseDoubleClick = (options: UsePreciseDoubleClickOptions = {}) => {
  const { timeThreshold = 250, distanceThreshold = 8 } = options;

  const clickState = useRef({
    lastClickTime: 0,
    lastX: 0,
    lastY: 0,
    doubleClicked: false,
  });

  const resetClickState = () => {
    clickState.current.lastClickTime = 0;
    clickState.current.lastX = 0;
    clickState.current.lastY = 0;
    clickState.current.doubleClicked = false;
  };

  const calculdateDistance = (pos: { x: number; y: number }) => {
    return Math.sqrt(Math.pow(pos.x - clickState.current.lastX, 2) + Math.pow(pos.y - clickState.current.lastY, 2));
  };

  const createPreciseDblClickHandlers = (onDoubleClick: (event: KonvaEventObject<MouseEvent>) => Promise<void>) => ({
    handlePreciseDblMouseDown: (event: KonvaEventObject<MouseEvent>) => {
      if (event.target === event.currentTarget && event.evt.button === 0) {
        const currentTime = Date.now();
        const pos = getEventPosition(event);

        const timeDiff = currentTime - clickState.current.lastClickTime;
        const distanceDiff = calculdateDistance(pos);

        if (timeDiff < timeThreshold && distanceDiff < distanceThreshold) {
          clickState.current.doubleClicked = true;
        }

        clickState.current.lastX = pos.x;
        clickState.current.lastY = pos.y;
        clickState.current.lastClickTime = currentTime;
      } else {
        resetClickState();
      }
    },

    handlePreciseDblMouseUp: (event: KonvaEventObject<MouseEvent>) => {
      const pos = getEventPosition(event);
      const distanceDiff = calculdateDistance(pos);

      if (distanceDiff >= distanceThreshold) {
        resetClickState();
      }

      if (clickState.current.doubleClicked) {
        onDoubleClick(event);
        resetClickState();
      }
    },
  });

  return {
    createPreciseDblClickHandlers,
  };
};
